import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Upload, Trash2, Zap, FileText, Bell, Settings,
  Shield, Building2, BarChart3, Lightbulb, Activity, Repeat, Gauge, QrCode,
  CalendarRange, FileDown, Plug, CreditCard, Rocket, HeartPulse, Crown,
  ScanLine, Brain, Search, Truck, Users, Wallet, Receipt, Archive, BookOpen,
  Phone, MessageCircle, Bot, Film, ClipboardList, TrendingUp, FileSearch,
  UserCog, KeyRound, LogOut, ArrowRight
} from "lucide-react";
import { useAuth } from "../App";

/**
 * Static catalogue of navigable pages, grouped by section.
 * Each command has: id, label, hint, icon, path, roles[], keywords[]
 */
const COMMANDS = [
  // ─── CMES PHARMA INTEL (destruction core) ───
  { id: "dashboard", label: "Tableau de bord", hint: "Vue d'ensemble pharmacie", icon: LayoutDashboard, path: "/dashboard", roles: ["pharmacy", "admin", "superadmin"], keywords: ["accueil", "home", "stats", "kpi"] },
  { id: "products", label: "Produits", hint: "Inventaire & stock", icon: Package, path: "/products", roles: ["pharmacy", "admin", "superadmin"], keywords: ["stock", "inventaire", "medicaments"] },
  { id: "scanner", label: "Scanner IA", hint: "Scanner emballages", icon: ScanLine, path: "/scanner", roles: ["pharmacy", "admin", "superadmin"], keywords: ["scan", "ocr", "qrcode"] },
  { id: "stock-intel", label: "Alertes Stock IA", hint: "Recommandations stock", icon: Brain, path: "/stock-intelligence", roles: ["pharmacy", "admin", "superadmin"], keywords: ["alerte", "ia", "prediction"] },
  { id: "import", label: "Import produits", hint: "Excel / CSV / PDF", icon: Upload, path: "/import", roles: ["pharmacy", "admin", "superadmin"], keywords: ["import", "excel", "csv"] },
  { id: "integration", label: "API Caisse", hint: "Connexion logiciel", icon: Plug, path: "/integration", roles: ["pharmacy", "admin", "superadmin"], keywords: ["api", "caisse", "winpharma"] },
  { id: "destructions", label: "Destructions", hint: "Demandes & suivi", icon: Trash2, path: "/destructions", roles: ["pharmacy", "admin", "superadmin"], keywords: ["destruction", "perimes"] },
  { id: "destruction-express", label: "Destruction Express", hint: "Demande urgente", icon: Zap, path: "/destruction-express", roles: ["pharmacy", "admin", "superadmin"], keywords: ["urgent", "express"] },
  { id: "qrcode", label: "QR Codes", hint: "Génération QR", icon: QrCode, path: "/qrcode", roles: ["pharmacy", "admin", "superadmin"], keywords: ["qr", "code"] },
  { id: "reports", label: "Rapports", hint: "Rapports comptables", icon: FileText, path: "/reports", roles: ["pharmacy", "admin", "superadmin"], keywords: ["rapport", "comptable"] },
  { id: "analytics", label: "Analyse", hint: "Statistiques avancées", icon: BarChart3, path: "/analytics", roles: ["pharmacy", "admin", "superadmin"], keywords: ["analytics", "analyse", "stats"] },
  { id: "recommendations", label: "Conseils IA", hint: "Recommandations", icon: Lightbulb, path: "/recommendations", roles: ["pharmacy", "admin", "superadmin"], keywords: ["conseil", "ia", "tips"] },
  { id: "sentinel", label: "Sentinel", hint: "Surveillance", icon: Activity, path: "/sentinel", roles: ["pharmacy", "admin", "superadmin"], keywords: ["surveillance", "alertes"] },
  { id: "itpp", label: "Indicateurs ITPP", hint: "Performance pharmacie", icon: Gauge, path: "/itpp", roles: ["pharmacy", "admin", "superadmin"], keywords: ["itpp", "indicateurs", "performance"] },
  { id: "exchange", label: "PharmExchange", hint: "Échange entre confrères", icon: Repeat, path: "/exchange", roles: ["pharmacy", "admin", "superadmin"], keywords: ["exchange", "echange"] },
  { id: "monthly", label: "Suivi Mensuel", hint: "Comparatif mensuel", icon: CalendarRange, path: "/monthly", roles: ["pharmacy", "admin", "superadmin"], keywords: ["mensuel", "mois", "comparaison"] },
  { id: "notifications", label: "Notifications", hint: "Alertes système", icon: Bell, path: "/notifications", roles: ["pharmacy", "admin", "superadmin"], keywords: ["notif", "alertes"] },
  { id: "downloads", label: "Documents", hint: "Téléchargements", icon: FileDown, path: "/downloads", roles: ["pharmacy", "admin", "superadmin"], keywords: ["doc", "download"] },

  // ─── CMES LOG (factures / créances / pipeline) ───
  { id: "log-hub", label: "Hub CMES-LOG", hint: "Accueil module logistique", icon: Building2, path: "/log", roles: ["pharmacy", "admin", "superadmin", "coursier"], keywords: ["log", "logistique", "hub"] },
  { id: "log-invoices", label: "Factures (LOG)", hint: "Factures pharmacie", icon: Receipt, path: "/log/invoices", roles: ["pharmacy", "admin", "superadmin"], keywords: ["facture", "invoice", "log"] },
  { id: "log-creances", label: "Créances", hint: "Suivi paiements", icon: Wallet, path: "/log/payment-tracking", roles: ["pharmacy", "admin", "superadmin"], keywords: ["paiement", "creance", "dette", "impaye"] },
  { id: "log-insurers", label: "Assureurs", hint: "Compagnies & taux", icon: Shield, path: "/log/insurers", roles: ["pharmacy", "admin", "superadmin"], keywords: ["assurance", "assureur", "cnam", "mutuelle"] },
  { id: "log-bank", label: "Relevés bancaires", hint: "Réconciliation", icon: CreditCard, path: "/log/bank-statements", roles: ["pharmacy", "admin", "superadmin"], keywords: ["banque", "releve", "rapprochement"] },
  { id: "log-bordereaux", label: "Bordereaux", hint: "Pipeline chèques/remises", icon: ClipboardList, path: "/log/bordereaux", roles: ["pharmacy", "admin", "superadmin"], keywords: ["bordereau", "remise", "cheque", "pipeline"] },
  { id: "log-expeditions", label: "Expéditions", hint: "Suivi livraisons", icon: Truck, path: "/log/expeditions", roles: ["pharmacy", "admin", "superadmin"], keywords: ["expedition", "livraison"] },
  { id: "log-courses", label: "Courses", hint: "Demandes coursier", icon: Truck, path: "/log/courses", roles: ["pharmacy", "admin", "superadmin", "coursier"], keywords: ["course", "coursier"] },
  { id: "log-analytics", label: "Analyse LOG", hint: "KPI logistique", icon: TrendingUp, path: "/log/analytics", roles: ["pharmacy", "admin", "superadmin"], keywords: ["analytics", "log", "kpi"] },
  { id: "log-archives", label: "Archives factures", hint: "Photos & documents", icon: Archive, path: "/log/archives", roles: ["pharmacy", "admin", "superadmin"], keywords: ["archive", "photo", "document"] },
  { id: "log-documentation", label: "Documentation LOG", hint: "Guides & docs", icon: BookOpen, path: "/log/documentation", roles: ["pharmacy", "admin", "superadmin"], keywords: ["doc", "guide", "tutoriel"] },
  { id: "log-guide", label: "Guide d'utilisation", hint: "Tutoriels vidéo", icon: Film, path: "/log/guide", roles: ["pharmacy", "admin", "superadmin", "coursier"], keywords: ["guide", "tuto", "video"] },
  { id: "log-mon-compte", label: "Mon compte", hint: "2FA, partage données…", icon: UserCog, path: "/log/mon-compte", roles: ["pharmacy", "admin", "superadmin", "coursier"], keywords: ["compte", "settings", "2fa", "parametres"] },
  { id: "log-tonnage", label: "Tonnage", hint: "Volume traité", icon: Gauge, path: "/log/tonnage", roles: ["pharmacy", "admin", "superadmin"], keywords: ["tonnage", "volume"] },
  { id: "log-alertes", label: "Alertes intelligentes", hint: "Alertes LOG", icon: Bell, path: "/log/alertes", roles: ["pharmacy", "admin", "superadmin"], keywords: ["alerte", "smart", "ia"] },
  { id: "log-messagerie", label: "Messagerie", hint: "Discussions internes", icon: MessageCircle, path: "/log/messagerie", roles: ["pharmacy", "admin", "superadmin"], keywords: ["chat", "message", "discussion"] },
  { id: "log-rapport-mensuel", label: "Rapport mensuel", hint: "Synthèse mensuelle", icon: FileText, path: "/log/rapport-mensuel", roles: ["pharmacy", "admin", "superadmin"], keywords: ["rapport", "mensuel"] },
  { id: "log-insurer-rankings", label: "Classement assureurs", hint: "Ranking national", icon: TrendingUp, path: "/log/insurer-rankings", roles: ["pharmacy", "admin", "superadmin"], keywords: ["classement", "ranking", "assureurs"] },

  // ─── Coursier / Agent commercial ───
  { id: "coursier-dashboard", label: "Tableau Coursier", hint: "Accueil agent", icon: Truck, path: "/coursier", roles: ["coursier", "admin", "superadmin"], keywords: ["coursier", "agent", "commercial"] },
  { id: "coursier-courses", label: "Mes courses", hint: "Courses assignées", icon: Truck, path: "/coursier/courses", roles: ["coursier", "admin", "superadmin"], keywords: ["course", "livraison"] },
  { id: "coursier-historique", label: "Historique courses", hint: "Courses terminées", icon: ClipboardList, path: "/coursier/historique", roles: ["coursier", "admin", "superadmin"], keywords: ["historique", "passe"] },
  { id: "coursier-notifications", label: "Notifications coursier", hint: "Alertes du jour", icon: Bell, path: "/coursier/notifications", roles: ["coursier", "admin", "superadmin"], keywords: ["notif", "alerte"] },

  // ─── Assurance ───
  { id: "assurance-dashboard", label: "Espace Assurance", hint: "Portail assureur", icon: Shield, path: "/assurance", roles: ["assurance", "admin", "superadmin"], keywords: ["assurance", "assureur"] },

  // ─── Admin / Superadmin ───
  { id: "admin", label: "Admin CMES", hint: "Tableau admin", icon: Shield, path: "/admin", roles: ["admin", "superadmin"], keywords: ["admin", "console"] },
  { id: "superadmin", label: "Super Admin", hint: "Vue globale plateforme", icon: Crown, path: "/superadmin", roles: ["superadmin"], keywords: ["superadmin", "plateforme"] },
  { id: "superadmin-accounts", label: "Gestion comptes", hint: "Approuver / suspendre", icon: Users, path: "/superadmin?tab=accounts", roles: ["superadmin"], keywords: ["comptes", "users", "approuver"] },
  { id: "superadmin-subs", label: "Gestion abonnements", hint: "Plans & paiements", icon: Crown, path: "/superadmin?tab=subscriptions", roles: ["superadmin"], keywords: ["abonnement", "subscription", "plan"] },
  { id: "superadmin-payments", label: "Suivi paiements mensuels", hint: "Recouvrement", icon: Wallet, path: "/superadmin?tab=payments", roles: ["superadmin"], keywords: ["paiement", "recouvrement"] },
  { id: "superadmin-analytics-vitrine", label: "Analytics Vitrine", hint: "Visiteurs site", icon: BarChart3, path: "/superadmin/analytics-vitrine", roles: ["superadmin"], keywords: ["vitrine", "site", "visiteurs"] },
  { id: "superadmin-videos", label: "Vidéos commerciales", hint: "Studio IA", icon: Film, path: "/superadmin/training-videos", roles: ["superadmin"], keywords: ["video", "studio", "commercial"] },
  { id: "deployment", label: "Mise en production", hint: "Statut deploy", icon: Rocket, path: "/deployment", roles: ["admin", "superadmin"], keywords: ["deploy", "prod"] },
  { id: "system-status", label: "Statut système", hint: "Santé services", icon: HeartPulse, path: "/system-status", roles: ["admin", "superadmin"], keywords: ["status", "sante", "uptime"] },
];

// Normalize accents for searching
const stripAccents = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const scoreCommand = (cmd, q) => {
  if (!q) return 1;
  const qn = stripAccents(q);
  const haystack = stripAccents(`${cmd.label} ${cmd.hint || ""} ${(cmd.keywords || []).join(" ")} ${cmd.path}`);
  // Exact phrase boost
  if (haystack.includes(qn)) {
    if (stripAccents(cmd.label).startsWith(qn)) return 100;
    return 80;
  }
  // All tokens must appear
  const tokens = qn.split(/\s+/).filter(Boolean);
  if (tokens.every(t => haystack.includes(t))) return 50;
  return 0;
};

export const CommandPalette = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const role = user?.role || "pharmacy";

  // Filter commands by role
  const roleCommands = useMemo(
    () => COMMANDS.filter(c => c.roles.includes(role)),
    [role]
  );

  const results = useMemo(() => {
    const scored = roleCommands
      .map(c => ({ c, s: scoreCommand(c, query) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s);
    return scored.map(x => x.c).slice(0, 30);
  }, [roleCommands, query]);

  // Keyboard shortcut Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const trigger = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k";
      if (trigger) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Keep selected in view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.querySelector(`[data-idx="${selected}"]`);
    if (item) item.scrollIntoView({ block: "nearest" });
  }, [selected]);

  // Don't show if not logged in
  if (!user) return null;

  const handleSelect = (cmd) => {
    setOpen(false);
    if (cmd?.path) navigate(cmd.path);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected(s => Math.min(s + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected(s => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[selected]) handleSelect(results[selected]);
    }
  };

  return (
    <>
      {/* Floating trigger button (visible only on desktop top-right) */}
      <button
        onClick={() => setOpen(true)}
        data-testid="command-palette-trigger"
        title="Recherche rapide (Ctrl+K)"
        className="hidden md:flex fixed top-4 right-6 z-[60] items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/70 backdrop-blur-md border border-cyan-500/30 text-slate-300 hover:text-white hover:border-cyan-400/60 transition-all text-xs"
      >
        <Search className="w-3.5 h-3.5 text-cyan-400" />
        <span>Rechercher…</span>
        <kbd className="ml-2 px-1.5 py-0.5 rounded bg-slate-800 border border-white/10 text-[10px] font-mono text-slate-400">Ctrl K</kbd>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
          onClick={() => setOpen(false)}
          data-testid="command-palette-overlay"
        >
          <div
            className="bg-slate-900 border border-cyan-500/30 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="command-palette-modal"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <Search className="w-4 h-4 text-cyan-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
                onKeyDown={onKeyDown}
                placeholder="Rechercher une page, un module, un assureur…"
                className="flex-1 bg-transparent outline-none text-white placeholder-slate-500 text-sm"
                data-testid="command-palette-input"
              />
              <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-white/10 text-[10px] font-mono text-slate-400">ESC</kbd>
            </div>

            <div ref={listRef} className="max-h-[55vh] overflow-y-auto py-2">
              {results.length === 0 && (
                <div className="text-center text-slate-500 text-sm py-10">
                  <FileSearch className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  Aucun résultat pour <span className="text-slate-300">"{query}"</span>
                </div>
              )}

              {results.map((cmd, idx) => {
                const Icon = cmd.icon;
                const active = idx === selected;
                return (
                  <button
                    key={cmd.id}
                    data-idx={idx}
                    data-testid={`command-item-${cmd.id}`}
                    onMouseEnter={() => setSelected(idx)}
                    onClick={() => handleSelect(cmd)}
                    className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-all ${
                      active
                        ? "bg-cyan-500/10 border-l-2 border-cyan-500"
                        : "border-l-2 border-transparent hover:bg-white/5"
                    }`}
                  >
                    <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${active ? "bg-cyan-500/20 text-cyan-300" : "bg-slate-800 text-slate-400"}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-white truncate">{cmd.label}</span>
                      {cmd.hint && (
                        <span className="block text-[11px] text-slate-500 truncate">{cmd.hint}</span>
                      )}
                    </span>
                    <span className="text-[10px] text-slate-600 font-mono truncate hidden sm:block">{cmd.path}</span>
                    {active && <ArrowRight className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            <div className="border-t border-white/10 px-5 py-2 flex items-center justify-between text-[10px] text-slate-500 bg-slate-950/40">
              <span className="flex items-center gap-3">
                <span><kbd className="px-1 py-0.5 rounded bg-slate-800 border border-white/10 font-mono">↑</kbd> <kbd className="px-1 py-0.5 rounded bg-slate-800 border border-white/10 font-mono">↓</kbd> Naviguer</span>
                <span><kbd className="px-1 py-0.5 rounded bg-slate-800 border border-white/10 font-mono">↵</kbd> Ouvrir</span>
                <span><kbd className="px-1 py-0.5 rounded bg-slate-800 border border-white/10 font-mono">ESC</kbd> Fermer</span>
              </span>
              <span>{results.length} résultat{results.length > 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommandPalette;
